const defaultCriteria = [
  { field: 'technology', answer: 'technology', operator: 'equals' },
  { field: 'mount', answer: 'mount', operator: 'contains' },
  { field: 'electricHeat', answer: 'heat', operator: 'boolean' },
  { field: 'outsideWidthCm', answer: 'width', operator: 'max' }
];
function predicateFor(rule, answer) {
  if (rule.operator === 'equals') return value => value === answer;
  if (rule.operator === 'contains') return value => Array.isArray(value) && value.includes(answer);
  if (rule.operator === 'boolean') return value => value === (answer === 'yes');
  if (rule.operator === 'max') return value => Number.isFinite(value) && value <= Number(answer);
  throw new Error('Unknown criterion operator');
}
export function assessVariant(variant, facts, answers, now = new Date(), criteria = defaultCriteria) {
  const verified = new Set(facts?.verifiedFields || []);
  const unknown = [];
  const failed = [];
  const check = (field, required, predicate) => {
    if (!required) return;
    if (!verified.has(field) || facts?.[field] === undefined) unknown.push(field);
    else if (!predicate(facts[field])) failed.push(field);
  };
  const fresh = facts?.checkedAt && Number.isFinite(Date.parse(facts.checkedAt)) && now.getTime() >= Date.parse(facts.checkedAt) && now.getTime() - Date.parse(facts.checkedAt) <= 30 * 86400000;
  for (const rule of criteria) {
    const predicate = predicateFor(rule, answers[rule.answer]);
    check(rule.field, answers[rule.answer], predicate);
  }
  if (failed.length) return { status: 'excluded', failed, unknown };
  if (!fresh) unknown.push('freshness');

  if (!variant.available) unknown.push('availability');
  return { status: unknown.length ? 'pending' : 'match', failed, unknown, overBudget: Boolean(answers.budget && variant.price > answers.budget * 100), preference: answers.purpose === 'additional_heat' && verified.has('electricHeat') && facts?.electricHeat === true ? 1 : 0 };
}

export function evaluateCatalog(catalog, config, answers, now = new Date()) {
  if (config.schemaVersion !== 1 || !config.module || !config.facts) throw new Error('Invalid module');
  const rows = catalog.flatMap(product => product.variants.map(variant => {
    const facts = config.facts[variant.sku] || config.facts['variant:' + variant.id];
    return { product, variant, facts, ...assessVariant(variant, facts, answers, now, config.criteria || defaultCriteria) };
  }));
  return rows.sort((a, b) => Number(a.status === 'excluded') - Number(b.status === 'excluded') || Number(a.status === 'pending') - Number(b.status === 'pending') || Number(a.overBudget) - Number(b.overBudget) || b.preference - a.preference || a.variant.price - b.variant.price);
}

export function getGuidedState(catalog, config, input, now = new Date()) {
  const answers = { ...input };
  const prefix = {};
  const controls = {};
  const reset = [];
  const primary = config.questions?.[0]?.name || 'technology';
  let blocked = !answers[primary];
  const matches = values => evaluateCatalog(catalog, config, values, now).filter(row => row.status === 'match');
  for (const question of config.questions || []) {
    const condition = question.visibleWhen;
    const visible = !condition || (condition.notEquals !== undefined ? answers[condition.field] !== condition.notEquals : answers[condition.field] === condition.equals);
    const state = { visible, disabled: question.name !== primary && blocked, options: {}, minimum: null, invalid: false };
    controls[question.name] = state;
    if (!visible || state.disabled) {
      if (answers[question.name]) reset.push(question.name);
      answers[question.name] = '';
      continue;
    }
    if (question.type === 'select') {
      for (const option of question.options) {
        const candidate = { ...prefix, [question.name]: option.value };
        if (question.name === 'purpose' && option.value === 'additional_heat') candidate.heat = 'yes';
        state.options[option.value] = option.value === '' || matches(candidate).length > 0;
      }
      if (question.name !== primary && !question.options.some(option => option.value && state.options[option.value])) {
        state.visible = false;
        state.disabled = true;
        if (answers[question.name]) reset.push(question.name);
        answers[question.name] = '';
        continue;
      }
      if (!answers[question.name] && !question.options.some(option => option.value === '')) {
        answers[question.name] = question.options.find(option => state.options[option.value])?.value || '';
      }
      if (answers[question.name] && !state.options[answers[question.name]]) {
        reset.push(question.name);
        answers[question.name] = question.options.find(option => state.options[option.value])?.value || '';
      }
      prefix[question.name] = answers[question.name] || '';
      if (question.name === primary) blocked = !prefix[primary];
    } else if (question.name === 'width') {
      const measured = matches({ ...prefix, width: Number.MAX_VALUE });
      state.minimum = measured.length ? Math.min(...measured.map(row => row.facts.outsideWidthCm)) : null;
      if (answers.width) {
        state.invalid = matches({ ...prefix, width: Number(answers.width) }).length === 0;
        prefix.width = Number(answers.width);
        if (state.invalid) blocked = true;
      }
    } else if (question.name === 'budget') {
      const rows = matches(prefix);
      state.minimum = rows.length ? Math.min(...rows.map(row => row.variant.price)) / 100 : null;
      prefix.budget = answers.budget ? Number(answers.budget) : null;
    }
  }
  return { answers, controls, reset, ready: Boolean(answers[primary]) && !controls.width?.invalid, count: matches(prefix).length };
}

if (typeof window !== 'undefined' && !customElements.get('starhome-product-advisor')) {
  class ProductAdvisor extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.catalog = JSON.parse(this.querySelector('[data-catalog]').textContent);
      this.form = this.querySelector('form');
      this.form.querySelectorAll('input, select').forEach(control => { control.disabled = true; });
      this.status = this.querySelector('[data-status]');
      this.results = this.querySelector('[data-results]');
      this.form.addEventListener('change', () => this.updateGuidance());
      this.form.addEventListener('input', event => { if (event.target.type === 'number') this.updateGuidance(); });
      this.form.addEventListener('submit', event => { event.preventDefault(); if (this.config) this.render(); });
      this.querySelector('[data-reset]').addEventListener('click', () => {
        this.form.querySelectorAll('input').forEach(input => { input.value = ''; });
        this.form.querySelectorAll('select').forEach(select => { select.selectedIndex = 0; });
        this.updateGuidance();
        this.results.replaceChildren();
        this.status.textContent = '';
        this.form.querySelector('select').focus();
      });
      try {
        const response = await fetch(this.dataset.configUrl, { credentials: 'omit' });
        if (!response.ok) throw new Error('Missing data');
        this.config = await response.json();
        if (this.config.schemaVersion !== 1 || this.config.module !== this.dataset.module || !this.config.facts) throw new Error('Invalid module');
        if (this.config.questions) this.buildQuestions(this.config.questions);
        this.form.querySelectorAll('input, select').forEach(control => { control.disabled = false; });
        this.updateGuidance();
      } catch { this.status.textContent = this.labels.error; }
    }
    buildQuestions(questions) {
      if (!Array.isArray(questions) || questions.length > 20) throw new Error('Invalid questions');
      const previous = new FormData(this.form);
      const submit = this.form.querySelector('[type=submit]');
      const reset = this.form.querySelector('[data-reset]');
      const fields = questions.map(question => {
        if (!question.name || !question.label || !['select', 'number'].includes(question.type)) throw new Error('Invalid question');
        const label = document.createElement('label');
        const caption = document.createElement('span');
        caption.textContent = question.label; label.append(caption);
        if (question.visibleWhen) label.dataset.visibleWhen = JSON.stringify(question.visibleWhen);
        const input = document.createElement(question.type === 'select' ? 'select' : 'input');
        input.name = question.name;
        input.id = `${this.id}-${question.name}`; label.htmlFor = input.id;
        caption.id = `${input.id}-label`; input.setAttribute('aria-labelledby', caption.id);
        if (question.type === 'select') {
          if (!Array.isArray(question.options)) throw new Error('Invalid options');
          for (const choice of question.options) {
            const option = document.createElement('option');
            option.value = choice.value; option.textContent = choice.label; input.append(option);
          }
        } else {
          input.type = 'number'; input.min = String(question.min ?? 0.1); input.step = String(question.step ?? 0.1);
        }
        if (previous.has(question.name)) input.value = String(previous.get(question.name));
        label.append(input);
        if (question.help) {
          const help = document.createElement('span'); help.className = 'starhome-advisor-help';
          help.id = `${input.id}-help`; input.setAttribute('aria-describedby', help.id);
          help.textContent = question.help; label.append(help);
        }
        return label;
      });
      const actions = document.createElement('div');
      actions.className = 'starhome-advisor-actions'; actions.append(submit, reset);
      this.form.replaceChildren(...fields, actions);
    }
    updateQuestionVisibility() {
      this.form.querySelectorAll('[data-visible-when]').forEach(label => {
        const condition = JSON.parse(label.dataset.visibleWhen);
        const value = this.form.elements.namedItem(condition.field)?.value;
        const visible = condition.notEquals !== undefined ? value !== condition.notEquals : value === condition.equals;
        label.hidden = !visible;
        label.querySelector('input, select').disabled = !visible;
      });
    }
    updateGuidance() {
      if (!this.config) return;
      const values = Object.fromEntries(Array.from(this.form.querySelectorAll('input, select'), control => [control.name, control.value]));
      const guidance = getGuidedState(this.catalog, this.config, values);
      const messages = [];
      for (const question of this.config.questions) {
        const control = this.form.elements.namedItem(question.name);
        const state = guidance.controls[question.name];
        const label = control.closest('label');
        label.hidden = !state.visible;
        control.disabled = !state.visible || state.disabled;
        if (control.tagName === 'SELECT') {
          for (const option of control.options) option.disabled = state.options[option.value] === false;
        }
        control.value = guidance.answers[question.name] ?? '';
        control.setCustomValidity('');
        let hint = label.querySelector('[data-guidance-help]');
        if (!hint) {
          hint = document.createElement('span'); hint.className = 'starhome-advisor-help'; hint.dataset.guidanceHelp = '';
          hint.id = control.id + '-guidance'; label.append(hint);
          control.setAttribute('aria-describedby', [control.getAttribute('aria-describedby'), hint.id].filter(Boolean).join(' '));
        }
        hint.textContent = '';
        if (question.name === 'mount' && control.disabled) hint.textContent = 'Wählen Sie zuerst die Flammenart.';
        if (question.name === 'width' && state.minimum !== null) {
          const minimum = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(state.minimum);
          hint.textContent = `Passende Modelle beginnen bei ${minimum} cm Außenbreite.`;
          if (state.invalid) {
            const message = `Für diese Auswahl ist bis zu Ihrer Breitenangabe kein Modell verfügbar. Erhöhen Sie die Breite auf mindestens ${minimum} cm oder ändern Sie die Bauform bzw. Flammenart.`;
            control.setCustomValidity(message); messages.push(message);
          }
        } else if (question.name === 'width' && state.invalid) {
          const message = 'Für diese Breite gibt es in Ihrer Auswahl kein passendes Modell. Entfernen Sie die Breitenangabe oder ändern Sie die Auswahl.';
          control.setCustomValidity(message); messages.push(message);
        }
        if (question.name === 'budget' && state.minimum !== null) {
          const minimum = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(state.minimum);
          hint.textContent = `Passende Ausführungen ab ${minimum}.`;
          if (control.value && Number(control.value) < state.minimum) hint.textContent += ' Die Vorschläge liegen über Ihrem Budget; Sie können die Grenze anpassen.';
        }
      }
      this.querySelector('[type=submit]').disabled = !guidance.ready;
      this.results.replaceChildren();
      const feedback = this.querySelector('[data-guidance]');
      feedback.textContent = messages.join(' ') || (guidance.reset.length && values.technology ? 'Ihre Auswahl wurde angepasst. Nicht passende Folgeantworten wurden zurückgesetzt.' : guidance.ready ? 'Die weiteren Auswahlmöglichkeiten passen zu Ihren bisherigen Antworten.' : 'Starten Sie mit der Flammenart. Danach stehen Ihnen die passenden Auswahlmöglichkeiten zur Verfügung.');
      this.status.textContent = '';
    }
    render() {
      const formData = new FormData(this.form);
      const answers = Object.fromEntries(formData);
      answers.width = formData.get('width') ? Number(formData.get('width')) : null;
      answers.budget = formData.get('budget') ? Number(formData.get('budget')) : null;
      const rows = evaluateCatalog(this.catalog, this.config, answers);
      this.results.replaceChildren();
      const matching = rows.filter(row => row.status === 'match');
      const pending = rows.filter(row => row.status === 'pending');
      this.status.textContent = matching.length ? `${this.labels.match}: ${matching.length}.` : this.labels.none;
      const shown = matching.slice(0, 12);
      for (const row of shown) {
        const card = document.createElement('article');
        const header = document.createElement('div');
        header.className = 'sh-advisor-card-header';
        const body = document.createElement('div');
        body.className = 'sh-advisor-card-body';
        const title = document.createElement('h4');
        title.textContent = `${row.product.title} — ${row.variant.title}`;
        const state = document.createElement('p');
        state.className = 'sh-advisor-card-match';
        state.textContent = row.status === 'pending' ? this.labels.pending : this.labels.match;
        const reason = document.createElement('p');
        reason.textContent = this.labels.verify;
        const price = document.createElement('p');
        price.className = 'sh-advisor-card-price';
        price.textContent = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(row.variant.price / 100) + (row.overBudget ? ` · ${this.labels.overBudget}` : '');
        const link = document.createElement('a');
        const url = new URL(row.product.url, location.origin);
        url.searchParams.set('variant', row.variant.id);
        link.href = url.href;
        link.textContent = this.labels.details;
        link.className = 'sh-advisor-card-link';
        const image = row.variant.image || row.product.image;
        if (image && image.url) {
          const media = document.createElement('a');
          media.className = 'sh-advisor-card-image';
          media.href = url.href;
          const thumbnail = document.createElement('img');
          thumbnail.src = image.url;
          thumbnail.alt = image.alt || row.product.title;
          thumbnail.width = 112;
          thumbnail.height = 112;
          thumbnail.loading = 'lazy';
          thumbnail.decoding = 'async';
          thumbnail.addEventListener('error', () => media.remove(), { once: true });
          media.append(thumbnail);
          header.append(media);
        }
        header.append(title);
        body.append(state, reason, price, link);
        card.append(header, body);

        this.results.append(card);
      }
      this.dispatchEvent(new CustomEvent('starhome:advisor_result', { bubbles: true, detail: { module: this.config.module, matches: matching.length, pending: pending.length, shown: shown.length } }));
      this.querySelector('[data-results-heading]').focus();
    }
  }
  customElements.define('starhome-product-advisor', ProductAdvisor);
}
